import { CloseOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { Button, Card, Col, Form, Input, InputNumber, message, Row, Tooltip } from 'antd'
import { GoldenTicketType, IBaseFormProps } from 'Modules'

import { INITIAL_GOLDEN_TICKET } from 'extends/pto-v2.extend'

import UploadSpecialImage from './upload-special-image'

interface IGoldenTicketProps extends IBaseFormProps {}

const GoldenTickets = ({ form, edit = true, mode = 'create' }: IGoldenTicketProps) => {
  // In edit mode, only allow editing Special Name and Description
  const canEdit = edit && mode !== 'edit'

  const getMaxNFTSupply = () => {
    return form.getFieldValue('maxNFTSupply') || 0
  }

  const getTotalQuantity = (excludeIndex?: number) => {
    const specials = form.getFieldValue('goldenTickets') || []
    return specials.reduce((total: number, item: GoldenTicketType, idx: number) => {
      if (excludeIndex !== undefined && idx === excludeIndex) return total
      return total + (item?.quantity || 0)
    }, 0)
  }

  // Check if any field in the item has a value
  const isItemPartiallyFilled = (index: number) => {
    const specials = form.getFieldValue('goldenTickets') || []
    const item = specials[index]
    if (!item) return false

    const hasName = item.name && item.name.trim() !== ''
    const hasDescription = item.description && item.description.trim() !== ''
    const hasQuantity = item.quantity && item.quantity > 0
    const hasImage = item.image && item.image.trim() !== ''

    return hasName || hasDescription || hasQuantity || hasImage
  }

  // Validate required field only if item is partially filled
  const validateRequiredIfPartiallyFilled = (fieldName: string, value: any, index: number) => {
    const isFilled = isItemPartiallyFilled(index)
    if (!isFilled) return Promise.resolve()

    if (fieldName === 'quantity') {
      if (!value || value <= 0) {
        return Promise.reject(new Error('Please input the quantity!'))
      }
    } else {
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        return Promise.reject(new Error(`Please input the ${fieldName.toLowerCase()}!`))
      }
    }
    return Promise.resolve()
  }

  const validateQuantity = (_: any, value: number, index: number) => {
    // Check if item is partially filled, then quantity is required
    const isFilled = isItemPartiallyFilled(index)
    if (isFilled && (!value || value <= 0)) {
      return Promise.reject(new Error('Please input the quantity!'))
    }

    // If no value, skip max supply validation
    if (!value || value <= 0) return Promise.resolve()

    // Validate against max supply
    const maxSupply = getMaxNFTSupply()
    if (!maxSupply) return Promise.resolve()

    const totalOthers = getTotalQuantity(index)
    const newTotal = totalOthers + value

    if (newTotal > maxSupply) {
      return Promise.reject(
        new Error(`Total quantity (${newTotal}) exceeds max supply (${maxSupply})`)
      )
    }
    return Promise.resolve()
  }

  const handleUploadSuccess = (index: number, filePath: string) => {
    const specials = form.getFieldValue('goldenTickets') || []
    specials[index] = {
      ...specials[index],
      image: filePath,
    }
    form.setFieldsValue({ goldenTickets: specials })
  }

  const handleRemoveImage = (index: number) => {
    const specials = form.getFieldValue('goldenTickets') || []
    specials[index] = {
      ...specials[index],
      image: '',
    }
    form.setFieldsValue({ goldenTickets: specials })
  }

  return (
    <fieldset>
      <legend>NFT Specials</legend>
      <Form.List name="goldenTickets">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }, index) => {
              const specials = form.getFieldValue('goldenTickets') || []
              const phases = form.getFieldValue('phases') || []
              const phaseUpcoming = phases.find((phase: any) => phase.name === 'upcoming')
              const currentImage = specials[index]?.image
              const isNewRecord = specials[index]?.isNewRecord
              const isDisabled = isNewRecord ? false : !canEdit
              const isAfterPhaseUpcoming =
                phaseUpcoming &&
                phaseUpcoming?.startTime &&
                phaseUpcoming?.startTime * 1000 < new Date().getTime()

              return (
                <Card
                  key={key}
                  size="small"
                  title={
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span>{`Special #${index + 1}`}</span>
                      {fields.length > 1 ? (
                        mode === 'create' || (edit && !isAfterPhaseUpcoming) ? (
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => {
                              if (mode === 'create' || !isAfterPhaseUpcoming) {
                                remove(name)
                              } else {
                                message.error(
                                  'You can not remove this special after phase upcoming'
                                )
                              }
                            }}
                          >
                            Remove
                          </Button>
                        ) : null
                      ) : null}
                    </div>
                  }
                  style={{ marginBottom: 16, borderRadius: 16, padding: 8 }}
                >
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item
                        {...restField}
                        label="Special Name"
                        name={[name, 'name']}
                        rules={[
                          {
                            validator: (_, value) =>
                              validateRequiredIfPartiallyFilled('Special Name', value, index),
                          },
                        ]}
                      >
                        <Input disabled={!edit} placeholder="Enter special name" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        {...restField}
                        label="Quantity to sell"
                        name={[name, 'quantity']}
                        rules={[
                          {
                            validator: (_, value) => validateQuantity(_, value, index),
                          },
                        ]}
                      >
                        <InputNumber
                          disabled={isDisabled}
                          max={getMaxNFTSupply() || undefined}
                          placeholder="Enter quantity"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        {...restField}
                        label="Description"
                        name={[name, 'description']}
                        rules={[
                          {
                            validator: (_, value) =>
                              validateRequiredIfPartiallyFilled('Description', value, index),
                          },
                        ]}
                      >
                        <Input.TextArea disabled={!edit} rows={3} placeholder="Enter description" />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        label={
                          <>
                            Upload Image
                            <Tooltip
                              title={
                                <div className="tooltip-box">
                                  <p className="tooltip-text" style={{ color: '#fff' }}>
                                    If no image is uploaded, this NFT will use the default image
                                    metadata
                                  </p>
                                </div>
                              }
                              placement="right"
                            >
                              <QuestionCircleOutlined className="tooltip-icon" />
                            </Tooltip>
                          </>
                        }
                      >
                        <UploadSpecialImage
                          currentImage={currentImage}
                          edit={!isDisabled}
                          onUploadSuccess={(filePath) => handleUploadSuccess(index, filePath)}
                          onRemove={() => handleRemoveImage(index)}
                        />
                        <p style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
                          You can only upload JPG/PNG file.
                        </p>
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              )
            })}
            {edit && fields.length < 6 && (
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add(INITIAL_GOLDEN_TICKET)}
                  block
                  icon={<PlusOutlined />}
                >
                  Add Another Special ({fields.length}/6)
                </Button>
              </Form.Item>
            )}
          </>
        )}
      </Form.List>
    </fieldset>
  )
}

export default GoldenTickets
